import { Avatar, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { formatDate } from '../functions/formatDate';
import { getUserInitials } from '../functions/getUserInitials';
import { IAttendance } from '../interfaces/attendance/IAttendance';
import { darkTheme, lightTheme } from '../mui/theme';

export const useAttendanceColumns = (
  setSelectedItem: (attendance: IAttendance) => void,
  setShowDeleteModal: (show: boolean) => void,
  hideActionColumn = false,
): GridColDef[] => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const iconFill =
    userData?.theme === ETheme.LIGHT
      ? lightTheme.palette.text.primary
      : darkTheme.palette.text.primary;
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'user',
      headerName: t('departments.employeeName'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            style={{ marginRight: '8px' }}
            src={params.row?.user?.profilePhoto?.thumbnail}
          >
            {getUserInitials(params.row?.user)}
          </Avatar>
          {params.row?.user?.displayName}
        </div>
      ),
    },
    {
      field: 'startDate',
      headerName: t('general.startDate'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { startDate } = params?.row;
        return <div>{formatDate(startDate, 'DD.MM.YYYY - HH:mm')}</div>;
      },
    },
    {
      field: 'endDate',
      headerName: t('general.endDate'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { endDate } = params?.row;
        return (
          <div>
            {endDate
              ? formatDate(endDate, 'DD.MM.YYYY - HH:mm')
              : 'In progress'}
          </div>
        );
      },
    },
    {
      field: 'spentTime',
      headerName: t('general.spentTime'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { endDate, startDate } = params?.row;
        const momentStartDate = moment(startDate); // Start time
        const momentEndDate = moment(endDate);
        const duration = moment.duration(momentEndDate?.diff(momentStartDate));
        const hours = duration && duration?.asHours()?.toFixed(2);

        return <div>{endDate ? `${hours} hours` : 'In progress'}</div>;
      },
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 150,
      sortable: false,
      headerClassName: hideActionColumn ? 'hideColumn' : '',
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '100%',
            }}
          >
            {/* Delete Icon */}
            {!isNormalUser && !hideActionColumn && (
              <Tooltip title={t('general.delete')}>
                <span
                  className={'d-flex'}
                  onClick={() => {
                    setSelectedItem(params?.row);
                    setShowDeleteModal(true);
                  }}
                >
                  <TrashIcon fill={iconFill} />
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
};
